import React from "react"
import { Image, Pressable, StyleSheet, Text, View } from "react-native"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import { theme } from "@common/theme/theme"
import parentSpacePhone from "@assets/inscription/espace-parent-telephone.png"
import IconDisc from "../components/IconDisc"
import InscriptionSheet from "../components/InscriptionSheet"
import { INSCRIPTION_PARENT_SPACE_GESTURES } from "../constants/inscriptionContent"

const DARK = theme.colors.primaryDark || theme.colors.primary

const PHONE_WIDTH = 170
// L'accueil dans son téléphone, tel que la médiathèque le compose : 840 × 1748.
const PHONE_RATIO = 1748 / 840

/** « Écrire au tuteur, décaler une séance, … » : les gestes, en une phrase. */
const gesturesSentence = `${INSCRIPTION_PARENT_SPACE_GESTURES.map(
    ({ label }, index) =>
        index === 0 ? label : label.charAt(0).toLowerCase() + label.slice(1)
).join(", ")}.`

/**
 * « Et après ? » — ce que le parent fera lui-même une fois inscrit, sans
 * appeler personne. La ligne se lit seule (elle nomme les gestes) ; la toucher
 * ouvre {@link ReservationParentSpaceSheet}.
 */
export const ReservationParentSpace = ({ onPress }) => (
    <Pressable
        style={styles.band}
        onPress={onPress}
        accessibilityRole="button"
        accessibilityLabel={`Ensuite, tout se gère depuis l'espace parent : ${gesturesSentence} Voir l'espace parent`}
    >
        <IconDisc name="mobile-alt" />
        <View style={styles.bandText}>
            <Text style={styles.lead}>
                Ensuite, tout se gère depuis l'espace parent
            </Text>
            <Text style={styles.gestures}>{gesturesSentence}</Text>
        </View>
        <FontAwesome5
            name="chevron-right"
            size={10}
            color={theme.colors.grey}
        />
    </Pressable>
)

/**
 * L'espace parent, montré : son accueil dans un téléphone, et les gestes
 * qu'on y fait. Même volet que les garanties.
 *
 * L'image est celle de la médiathèque
 * (`docs/media/produit/parent/accueil/appareils/accueil-telephone.png`),
 * copiée ici parce que l'app ne lit pas hors de son paquet : une recapture
 * là-bas se recopie.
 */
export const ReservationParentSpaceSheet = ({ isVisible, onClose }) => (
    <InscriptionSheet
        isVisible={isVisible}
        onClose={onClose}
        title="L'espace parent"
    >
        <Image
            source={parentSpacePhone}
            style={styles.phone}
            resizeMode="contain"
            accessibilityLabel="L'accueil de l'espace parent : la prochaine séance et les gestes du quotidien"
        />
        <View style={styles.grid}>
            {INSCRIPTION_PARENT_SPACE_GESTURES.map((gesture) => (
                <View key={gesture.label} style={styles.gesture}>
                    <IconDisc name={gesture.icon} />
                    <Text style={styles.gestureLabel}>{gesture.label}</Text>
                </View>
            ))}
        </View>
    </InscriptionSheet>
)

const styles = StyleSheet.create({
    band: {
        flexDirection: "row",
        alignItems: "center",
        gap: 12,
        marginTop: 10,
        paddingVertical: 12,
        paddingHorizontal: 14,
        borderRadius: 14,
        borderWidth: 1,
        borderColor: theme.colors.lightMore,
        backgroundColor: theme.colors.white,
    },
    bandText: { flex: 1, gap: 2 },
    lead: {
        fontFamily: "Montserrat-SemiBold",
        fontSize: 13,
        lineHeight: 18,
        color: DARK,
    },
    gestures: {
        fontFamily: "Montserrat-Regular",
        fontSize: 12,
        lineHeight: 17,
        color: theme.colors.greyAlt,
    },
    phone: {
        alignSelf: "center",
        width: PHONE_WIDTH,
        height: Math.round(PHONE_WIDTH * PHONE_RATIO),
    },
    grid: {
        flexDirection: "row",
        flexWrap: "wrap",
        rowGap: 12,
    },
    gesture: {
        width: "50%",
        flexDirection: "row",
        alignItems: "center",
        gap: 10,
        paddingRight: 8,
    },
    gestureLabel: {
        flex: 1,
        fontFamily: "Montserrat-SemiBold",
        fontSize: 13,
        lineHeight: 17,
        color: DARK,
    },
})
